import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TaskSelection from "./TaskSelection";
import FieldRenderer from "./FieldRenderer";
import {
  code,
  email,
  writing,
  reading,
  stock,
  prompt,
  improve_eng,
} from "./fields";
import { TEMPLATES } from "./params";

const PromptGenerator = () => {
  const { t } = useTranslation();
  const [selectedTask, setSelectedTask] = useState("");
  const [fields, setFields] = useState({});
  const [generatedPrompt, setGeneratedPrompt] = useState("");

  const tasks = useMemo(
    () => [
      { id: "code" },
      { id: "email" },
      { id: "improve_eng" },
      { id: "prompt" },
      { id: "writing" },
      { id: "reading" },
      { id: "stock" },
    ],
    []
  );

  const taskOptions = useMemo(
    () => ({
      code: code,
      email: email,
      writing: writing,
      reading: reading,
      stock: stock,
      prompt: prompt,
      improve_eng: improve_eng,
    }),
    []
  );

  useEffect(() => {
    updatePrompt();
  }, [selectedTask, fields]);

  const handleTaskSelection = (taskId) => {
    setSelectedTask(taskId);
    setFields({});
  };

  const handleFieldChange = (fieldId, value, updatedFields) => {
    setFields((prevFields) => ({
      ...prevFields,
      [fieldId]: value,
    }));

    if (updatedFields) {
      const currentTaskOptions = taskOptions[selectedTask];
      const updatedTaskOptions = currentTaskOptions.map((option) => {
        const updatedField = updatedFields.find((o) => o.id === option.id);
        return updatedField || option;
      });
      taskOptions[selectedTask] = updatedTaskOptions;
    }
  };

  function embedIntoTemplate(template, items, fields) {
    let result = template;
    items.forEach((item) => {
      const value = fields[item.id];
      // Use a regular expression to replace all occurrences of the placeholder
      result = result.replace(new RegExp(`{{${item.id}}}`, "g"), value);
    });
    return result;
  }

  function embedIntoLineTemplate(template, value) {
    let result = template;
    // Use a regular expression to replace all occurrences of the placeholder
    result = result.replace(new RegExp(`{{value}}`, "g"), value);
    return result;
  }

  const updatePrompt = () => {
    if (!selectedTask) {
      setGeneratedPrompt("");
      return;
    }

    if (selectedTask === "stock") {
      const embeddedString = embedIntoTemplate(
        TEMPLATES.companyAnalysis,
        taskOptions[selectedTask],
        fields
      );
      setGeneratedPrompt(embeddedString);
      return;
    } else if (selectedTask === "prompt") {
      const embeddedString = embedIntoTemplate(
        TEMPLATES.promptImprovement,
        taskOptions[selectedTask],
        fields
      );
      setGeneratedPrompt(embeddedString);
      return;
    }

    let prompt = "";
    taskOptions[selectedTask].forEach((option) => {
      if (option.conditional && !option.conditional(fields)) {
        return;
      }
      const value = fields[option.id];
      if (value && (!option.optional || value.trim() !== "")) {
        if (option.template) {
          let temp = value;
          if (temp === "OTHER_VALUE") {
            temp = fields[`${option.id}_other`];
          } else if (Array.isArray(temp)) {
            temp = temp.join(option.separator || ", ");
          }
          prompt += embedIntoLineTemplate(option.template, temp) + "\n\n";
        } else {
          const optionName = t(`promptGenerator.options.${option.id}`);
          prompt += `${optionName}: ${
            Array.isArray(value) ? value.join(", ") : value
          }\n`;
        }
      }
    });

    setGeneratedPrompt(prompt.trim());
  };

  const copyPrompt = () => {
    navigator.clipboard
      .writeText(generatedPrompt)
      .then(() => alert(t("promptGenerator.promptCopied")))
      .catch((err) => console.error("Failed to copy: ", err));
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="mb-8">
            <TaskSelection
              tasks={tasks}
              selectedTask={selectedTask}
              onTaskSelect={handleTaskSelection}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {selectedTask && (
              <div>
                <FieldRenderer
                  fields={taskOptions[selectedTask]}
                  selectedFields={fields}
                  onFieldChange={handleFieldChange}
                />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("promptGenerator.generatedPrompt")}
              </h3>
              <div className="relative">
                <textarea
                  className="w-full h-64 p-3 border rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={generatedPrompt}
                  readOnly
                ></textarea>
                <button
                  className="absolute top-2 right-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-3 rounded-md text-sm transition duration-300 ease-in-out"
                  onClick={copyPrompt}
                  disabled={!generatedPrompt}
                >
                  {t("promptGenerator.copyPrompt")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptGenerator;
